<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/testing/safety">Safety Tests</a></li>
        <li class="breadcrumb-item active">Test Report</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">Test Report</h1>
  </div>
  <div class="d-flex gap-2">
    <button class="btn btn-outline-secondary" (click)="printReport()">
      <i class="bi bi-printer me-2"></i>Print
    </button>
    <button class="btn btn-outline-secondary" (click)="goBack()">
      <i class="bi bi-arrow-left me-2"></i>Back
    </button>
  </div>
</div>

<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading test report...</p>
</div>

<!-- Error State -->
<div *ngIf="!isLoading && error" class="mw-700px mx-auto">
  <div class="alert alert-danger">
    <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
  </div>
  <button class="btn btn-outline-secondary" (click)="goBack()">
    <i class="bi bi-arrow-left me-2"></i>Back to Safety Tests
  </button>
</div>

<div *ngIf="!isLoading && !error && report">
  <!-- Test Summary Card -->
  <div class="card mb-4">
    <div class="card-body p-4">
      <div class="row">
        <div class="col-lg-8">
          <div class="d-flex align-items-start gap-3">
            <div class="test-icon">
              <i [class]="'bi ' + report.icon"></i>
            </div>
            <div class="flex-grow-1">
              <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <h3 class="mb-0">{{ report.testTitle }}</h3>
                <span class="badge" [ngClass]="getStatusBadgeClass(report.status)">
                  {{ getStatusText(report.status) }}
                </span>
              </div>
              <p class="text-muted mb-3">{{ report.description }}</p>

              <div class="d-flex flex-wrap gap-4 small">
                <div>
                  <span class="text-muted">Completed:</span>
                  <span class="fw-medium ms-1">{{ report.currentAttempt.date | adnDate:'datetime' }}</span>
                </div>
                <div>
                  <span class="text-muted">Duration:</span>
                  <span class="fw-medium ms-1">{{ report.currentAttempt.duration }}</span>
                </div>
                <div *ngIf="report.expirationDate">
                  <span class="text-muted">Expires:</span>
                  <span class="fw-medium ms-1">{{ report.expirationDate | adnDate }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Score Display -->
        <div class="col-lg-4 mt-4 mt-lg-0">
          <div class="score-display text-center" [ngClass]="getScoreClass(report.currentAttempt.score, report.currentAttempt.passingScore)">
            <div class="score-value">{{ report.currentAttempt.score }}%</div>
            <div class="score-label">{{ getScoreLabel(report.currentAttempt.score) }}</div>
            <div class="score-details mt-2">
              {{ report.currentAttempt.questionsCorrect }} of {{ report.currentAttempt.totalQuestions }} correct
            </div>
            <div class="passing-score mt-1">
              Passing: {{ report.currentAttempt.passingScore }}%
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Quick Stats Row -->
  <div class="row g-4 mb-4">
    <div class="col-md-3">
      <div class="card stat-card">
        <div class="card-body text-center">
          <div class="stat-icon text-success">
            <i class="bi bi-check-circle"></i>
          </div>
          <div class="stat-value">{{ report.currentAttempt.questionsCorrect }}</div>
          <div class="stat-label">Correct Answers</div>
        </div>
      </div>
    </div>
    <div class="col-md-3">
      <div class="card stat-card">
        <div class="card-body text-center">
          <div class="stat-icon text-danger">
            <i class="bi bi-x-circle"></i>
          </div>
          <div class="stat-value">{{ report.currentAttempt.totalQuestions - report.currentAttempt.questionsCorrect }}</div>
          <div class="stat-label">Incorrect Answers</div>
        </div>
      </div>
    </div>
    <div class="col-md-3">
      <div class="card stat-card">
        <div class="card-body text-center">
          <div class="stat-icon text-primary">
            <i class="bi bi-clock"></i>
          </div>
          <div class="stat-value">{{ report.currentAttempt.duration }}</div>
          <div class="stat-label">Time Taken</div>
        </div>
      </div>
    </div>
    <div class="col-md-3">
      <div class="card stat-card">
        <div class="card-body text-center">
          <div class="stat-icon text-info">
            <i class="bi bi-arrow-repeat"></i>
          </div>
          <div class="stat-value">{{ report.previousAttempts.length + 1 }}</div>
          <div class="stat-label">Total Attempts</div>
        </div>
      </div>
    </div>
  </div>

  <div class="row g-4">
    <!-- Left Column -->
    <div class="col-lg-8">
      <!-- Category Breakdown -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-bar-chart me-2"></i>Performance by Category</h5>
        </div>
        <div class="card-body">
          <div class="category-list">
            <div *ngFor="let cat of report.categoryBreakdown" class="category-item">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="category-name">{{ cat.category }}</span>
                <span class="category-score">{{ cat.correct }}/{{ cat.total }} ({{ cat.percentage }}%)</span>
              </div>
              <div class="progress h-8px">
                <div class="progress-bar" [ngClass]="getCategoryBarClass(cat.percentage)"
                     [style.width.%]="cat.percentage"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Question Review -->
      <div class="card mb-4">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-list-check me-2"></i>Question Review</h5>
          <div class="d-flex align-items-center gap-2">
            <span class="small text-muted">{{ displayedQuestions.length }} questions</span>
            <button class="btn btn-sm" [class.btn-outline-danger]="showOnlyIncorrect"
                    [class.btn-outline-secondary]="!showOnlyIncorrect"
                    (click)="toggleQuestionFilter()" [disabled]="incorrectCount === 0">
              <i class="bi bi-funnel me-1"></i>
              {{ showOnlyIncorrect ? 'Show All' : 'Show Incorrect (' + incorrectCount + ')' }}
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="question-list">
            <div *ngFor="let q of displayedQuestions; let i = index" class="question-item"
                 [class.question-correct]="q.isCorrect" [class.question-incorrect]="!q.isCorrect">
              <div class="question-header">
                <div class="d-flex align-items-start gap-3">
                  <div class="question-number" [class.bg-success]="q.isCorrect" [class.bg-danger]="!q.isCorrect">
                    {{ q.questionNumber }}
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <span class="badge bg-secondary">{{ q.category }}</span>
                      <span *ngIf="q.isCorrect" class="text-success small">
                        <i class="bi bi-check-circle-fill me-1"></i>Correct
                      </span>
                      <span *ngIf="!q.isCorrect" class="text-danger small">
                        <i class="bi bi-x-circle-fill me-1"></i>Incorrect
                      </span>
                    </div>
                    <p class="question-text mb-3">{{ q.question }}</p>

                    <div class="answer-options">
                      <div *ngFor="let option of q.options; let optIndex = index" class="answer-option"
                           [class.correct-answer]="option.id === q.correctOptionId"
                           [class.user-answer]="option.id === q.userOptionId && !q.isCorrect"
                           [class.user-correct]="option.id === q.userOptionId && q.isCorrect">
                        <span class="option-letter">{{ ['A', 'B', 'C', 'D', 'E', 'F'][optIndex] }}</span>
                        <span class="option-text">{{ option.text }}</span>
                        <span *ngIf="option.id === q.correctOptionId" class="option-indicator">
                          <i class="bi bi-check-lg"></i>
                        </span>
                        <span *ngIf="option.id === q.userOptionId && !q.isCorrect" class="option-indicator text-danger">
                          <i class="bi bi-x-lg"></i>
                        </span>
                      </div>
                    </div>

                    <div *ngIf="q.explanation && !q.isCorrect" class="explanation mt-3">
                      <div class="explanation-header">
                        <i class="bi bi-lightbulb me-2"></i>Explanation
                      </div>
                      <p class="mb-0">{{ q.explanation }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div *ngIf="displayedQuestions.length === 0 && showOnlyIncorrect" class="text-center py-5">
              <i class="bi bi-emoji-smile text-success fs-3rem"></i>
              <p class="text-muted mt-3 mb-0">All questions were answered correctly!</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column -->
    <div class="col-lg-4">
      <!-- Certificate Card (if passed) -->
      <div *ngIf="report.status === 'passed' && report.certificateNumber" class="card mb-4 certificate-card">
        <div class="card-body text-center p-4">
          <div class="certificate-icon mb-3">
            <i class="bi bi-award"></i>
          </div>
          <h5 class="mb-1">Certificate of Completion</h5>
          <p class="text-muted small mb-3">{{ report.testTitle }}</p>
          <div class="certificate-number mb-3">
            <span class="label">Certificate #</span>
            <span class="number">{{ report.certificateNumber }}</span>
          </div>
          <div class="certificate-validity mb-3">
            <span class="text-muted small">Valid until:</span>
            <div class="fw-medium">{{ report.expirationDate | adnDate }}</div>
          </div>
          <button class="btn btn-primary w-100" (click)="downloadCertificate()">
            <i class="bi bi-download me-2"></i>Download Certificate
          </button>
        </div>
      </div>

      <!-- Previous Attempts -->
      <div *ngIf="report.previousAttempts.length > 0" class="card mb-4">
        <div class="card-header p-3">
          <h6 class="mb-0"><i class="bi bi-clock-history me-2"></i>Previous Attempts</h6>
        </div>
        <div class="card-body p-0">
          <div class="previous-attempts-list">
            <div *ngFor="let attempt of report.previousAttempts" class="attempt-item">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="small text-muted">{{ attempt.date | adnDate:'datetime' }}</div>
                  <div class="attempt-score" [class.text-success]="attempt.passed" [class.text-danger]="!attempt.passed">
                    {{ attempt.score }}% <span class="small">({{ attempt.questionsCorrect }}/{{ attempt.totalQuestions }})</span>
                  </div>
                </div>
                <span class="badge" [ngClass]="attempt.passed ? 'badge-passed' : 'badge-failed'">
                  {{ attempt.passed ? 'Passed' : 'Failed' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h6 class="mb-0"><i class="bi bi-lightning me-2"></i>Actions</h6>
        </div>
        <div class="card-body">
          <div class="d-grid gap-2">
            <button class="btn btn-outline-primary" (click)="retakeTest()">
              <i class="bi bi-arrow-repeat me-2"></i>Retake Test
            </button>
            <button class="btn btn-outline-secondary" (click)="printReport()">
              <i class="bi bi-printer me-2"></i>Print Report
            </button>
            <a routerLink="/logbook/testing/safety" class="btn btn-outline-secondary">
              <i class="bi bi-arrow-left me-2"></i>Back to Safety Tests
            </a>
          </div>
        </div>
      </div>

      <!-- Study Resources -->
      <div class="card">
        <div class="card-header p-3">
          <h6 class="mb-0"><i class="bi bi-book me-2"></i>Study Resources</h6>
        </div>
        <div class="card-body">
          <p class="small text-muted mb-3">Review these materials to improve your score on future attempts.</p>
          <ul class="resource-list">
            <li>
              <a href="#" class="resource-link">
                <i class="bi bi-file-pdf me-2"></i>Part 137 Study Guide
              </a>
            </li>
            <li>
              <a href="#" class="resource-link">
                <i class="bi bi-play-circle me-2"></i>Video: Agricultural Operations Overview
              </a>
            </li>
            <li>
              <a href="#" class="resource-link">
                <i class="bi bi-link-45deg me-2"></i>FAA Part 137 Regulations
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</div>
